import React, { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  consumeLoginReturnPath,
  extractTokensFromLoginResponse,
  loginWithOTP,
  normalizeIndiaPhone,
  parseAuthError,
  persistSession,
  returnPathFromRouterState,
  sendOTP,
} from '../../services/authService';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phoneDigits, setPhoneDigits] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inFlightRef = useRef(false);

  const performSendOtp = useCallback(
    async (digits10) => {
      const p = normalizeIndiaPhone(digits10);
      if (!p) {
        setError('Enter a valid 10-digit mobile number.');
        return;
      }
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setError(null);
      setLoading(true);
      try {
        await sendOTP({ phone: p });
        setOtpSent(true);
        setOtp('');
      } catch (err) {
        setError(parseAuthError(err));
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    []
  );

  const performLogin = useCallback(
    async (codeRaw, digits10ForPhone) => {
      const p = normalizeIndiaPhone(digits10ForPhone);
      const code = String(codeRaw || '')
        .replace(/\D/g, '')
        .slice(0, 6);
      if (!p) {
        setError('Enter a valid 10-digit mobile number.');
        return;
      }
      if (!code) {
        setError('Enter the OTP sent to your phone.');
        return;
      }
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setError(null);
      setLoading(true);
      try {
        const data = await loginWithOTP({ phone: p, otp: code });
        const { accessToken, refreshToken } = extractTokensFromLoginResponse(data);
        if (!accessToken) {
          setError(
            'Login succeeded but no access token was returned. Expected `profiles[].token` from POST /api/users/loginOTP.'
          );
          return;
        }
        if (!refreshToken) {
          setError(
            'Login succeeded but no refresh token was returned. Expected `profiles[].refresh_token` so the session can be renewed after the access token expires.'
          );
          return;
        }
        persistSession(accessToken, refreshToken);
        const next =
          consumeLoginReturnPath() || returnPathFromRouterState(location.state?.from) || '/wallets';
        navigate(next, { replace: true });
      } catch (err) {
        setError(parseAuthError(err));
      } finally {
        setLoading(false);
        inFlightRef.current = false;
      }
    },
    [location.state, navigate]
  );

  const handleSendOtp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    await performSendOtp(phoneDigits);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await performLogin(otp, phoneDigits);
  };

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4">
      <div className="card-surface w-full max-w-md p-8 shadow-lg shadow-slate-200/60">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25">
            <i className="material-icons-outlined text-3xl">lock_open</i>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">Wallet dashboard</p>
          <h1 className="page-title mt-1">Sign in</h1>
          <p className="page-subtitle mt-2">Use your registered mobile number. We will send a one-time password.</p>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <i className="material-icons-outlined shrink-0 text-lg">error_outline</i>
            <span>{error}</span>
          </div>
        )}

        <form className="grid gap-5" onSubmit={otpSent ? handleLogin : handleSendOtp}>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-700">Mobile number</span>
            <div className="flex rounded-lg border border-slate-200 bg-white shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
              <span className="flex items-center border-r border-slate-200 px-3 text-slate-500">+91</span>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="10-digit number"
                value={phoneDigits}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhoneDigits(next);
                  if (next.length === 10 && !otpSent && !loading) {
                    void performSendOtp(next);
                  }
                }}
                className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-slate-900 outline-none"
                disabled={loading}
              />
            </div>
          </label>

          {otpSent ? (
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-slate-700">One-time password</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(next);
                  if (next.length === 6 && otpSent && !loading) {
                    void performLogin(next, phoneDigits);
                  }
                }}
                className="rounded-lg border border-slate-200 px-3 py-2.5 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button type="submit" className="btn-primary w-full sm:flex-1" disabled={loading}>
              {loading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <i className="material-icons-outlined animate-spin text-base">refresh</i>
                  Please wait…
                </span>
              ) : otpSent ? (
                'Sign in'
              ) : (
                'Send OTP'
              )}
            </button>
            {otpSent ? (
              <button
                type="button"
                className="btn-secondary w-full sm:w-auto"
                disabled={loading}
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                  setError(null);
                }}
              >
                Change number
              </button>
            ) : null}
          </div>
        </form>

        {otpSent ? (
          <p className="mt-4 text-center text-xs text-slate-500">
            Did not receive it?{' '}
            <button
              type="button"
              className="font-medium text-primary-700 hover:underline"
              disabled={loading}
              onClick={handleSendOtp}
            >
              Resend OTP
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default LoginPage;
